import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, map } from 'rxjs';
import { WordTemplatesService } from 'src/app/services/word-templates/word-templates.service';
import { FormUtil } from 'src/app/utils/form.util';
import { upload, Upload, UploadState } from 'src/app/utils/upload.operator';

@Component({
    selector: 'app-dialog-upload-file',
    templateUrl: './dialog-upload-file.component.html',
    styleUrl: './dialog-upload-file.component.scss',
    imports: [CommonModule, MatProgressBarModule, MatButtonModule, MatIconModule],
})
export class DialogUploadFileComponent {
    private readonly progress = new BehaviorSubject<Upload | null>(null);
    public readonly progress$ = this.progress.asObservable();

    constructor(
        public dialogRef: MatDialogRef<DialogUploadFileComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            accept: string;
            id: string;
        },
        readonly snackBar: MatSnackBar,
        private readonly wordTemplatesService: WordTemplatesService,
    ) {}

    public onAdd(event: any, isDrop = false): void {
        if (event?.target?.files?.length || isDrop) {
            let files = isDrop ? Array.from(event) : Array.from(event.target.files);

            if (files.length > 1) {
                this.snackBar.open('Es kann nur ein Dokument hochgeladen werden', '', {
                    duration: 4000,
                });
            }
            files = files.splice(0, 1);

            this.loadFile(files);
        }
    }

    private loadFile(files: any): void {
        if (!files.length) {
            return;
        }

        for (const file of files) {
            const title = FormUtil.textToSlug(file.name);

            this.uploadWordTemplate(title, file);
        }
    }

    private uploadWordTemplate(title: string, file: any): void {
        if (file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
            this.snackBar.open('Es können nur Word-Dokumente hochgeladen werden', '', {
                duration: 4000,
            });

            return;
        }

        const obsv = this.data.id
            ? this.wordTemplatesService.update({ id: this.data.id, file })
            : this.wordTemplatesService.create({ dto: { name: title }, file });

        obsv.pipe(
            upload(title),
            map((res) => {
                this.progress.next(res);

                return res;
            }),
        ).subscribe((res) => {
            if (res.state === UploadState.DONE && res.response) {
                this.progress.next(null);
                this.dialogRef.close(res.response);
            }
        });
    }

    public get mode(): 'indeterminate' | 'determinate' {
        if (this.progress.value?.state === UploadState.IN_PROGRESS && this.progress.value?.progress === 100) {
            return 'indeterminate';
        }
        return 'determinate';
    }

    public get info(): string {
        if (this.progress.value?.state === UploadState.PENDING) {
            return 'Pending';
        }

        if (this.progress.value?.state === UploadState.IN_PROGRESS) {
            if (this.progress.value?.progress === 100) {
                return 'Converting';
            }
            return 'Upload';
        }

        return 'Done';
    }
}
